/**
 * The single source of truth for where this app is mounted.
 *
 * Derived from `import.meta.env.BASE_URL`, which Vite computes from the `base` option and guarantees to
 * be normalised: always a leading slash, always a trailing slash, and exactly `"/"` at site root.
 *
 * Deliberately NOT derived from `VITE_BASE_URL`, for two reasons:
 *  1. It is raw unvalidated input. Across the ten .env files it appears both with and without a
 *     trailing slash, which is why the code that consumed it grew three different join implementations.
 *  2. It can disagree with what the bundler actually emitted. `BASE_URL` is the same value Vite used to
 *     rewrite asset URLs in index.html, so app links and assets cannot drift apart. `VITE_BASE_URL` has
 *     no such guarantee.
 *
 * Also deliberately NOT derived from `window.location.pathname`. Sniffing the current URL reports where
 * the *user* is, not where the *app* is mounted, and the two are indistinguishable in general -- which
 * is why the previous implementation needed a hardcoded blocklist of route names
 * (`["dashboard", "verify", "profile", ...]`) and still returned the wrong answer at site root.
 */

/** Normalised prefix: `""` at site root, otherwise e.g. `"/SEPDev"`. Never has a trailing slash. */
export const BASE_PATH: string = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");

/**
 * Joins an app-relative path onto {@link BASE_PATH}, tolerating a leading slash or not.
 * Always returns a path with exactly one leading slash and no doubled separators.
 */
export function withBase(path: string): string {
    const trimmed = (path ?? "").replace(/^\/+/, "");

    if (trimmed.length === 0) {
        // `basename` and root links want "/" rather than "".
        return BASE_PATH || "/";
    }

    return `${BASE_PATH}/${trimmed}`;
}

/** Same-origin absolute URL for an app-relative path. */
export function absoluteUrl(path: string): string {
    return `${window.location.origin}${withBase(path)}`;
}
